import { useQueryClient } from '@tanstack/react-query';
import type { BatchParams } from '@dealstack/frontend/app/services/dealstackAi/dealstackAi.types';
import { httpStatusOf } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	ingestionProgressFilters,
	useDeleteIngestionBatchMutation,
} from '../Ingestion/hooks/useIngestionQueries';
import {
	batchListsFilters,
	batchQueriesFilters,
} from '../AnalysisWorkspace/hooks/useDealstackAiQueries';
import type {
	DeleteBatchRequest,
	DeleteFailure,
	UseDeleteBatchResult,
} from './batchDeletion.types';
import { deleteFailureOf } from './batchDeletion.utils';

const FAILURE_TOAST_KEYS: Record<DeleteFailure, string> = {
	still_processing: 'dealstack_ai.batch_delete.toast.still_processing',
	has_review_items: 'dealstack_ai.batch_delete.toast.has_review_items',
	cancelled: 'dealstack_ai.batch_delete.toast.error',
	other: 'dealstack_ai.batch_delete.toast.error',
};

/**
 * "Delete batch" with its toasts. A 404 means the batch is already gone, so it ends like a delete; a refusal for
 * something to review re-reads the batch, which takes the button away.
 */
export const useDeleteBatch = (params: BatchParams): UseDeleteBatchResult => {
	const tCapManage = useTranslationCapManage();
	const queryClient = useQueryClient();
	const mutation = useDeleteIngestionBatchMutation(params);

	const deleted = ({ kind, name, onDeleted }: DeleteBatchRequest) => {
		showToastV2(
			kind === 'failed'
				? {
						state: 'success',
						title: tCapManage('dealstack_ai.batch_delete.toast.failed_title'),
						message: tCapManage('dealstack_ai.batch_delete.toast.failed_message'),
					}
				: {
						state: 'success',
						title: tCapManage('dealstack_ai.batch_delete.toast.empty_title', { name }),
					}
		);
		onDeleted();
		// After leaving: a cached read of the deleted batch would otherwise come back on Back.
		queryClient.removeQueries(batchQueriesFilters(params.batchId));
		queryClient.removeQueries(ingestionProgressFilters(params.batchId));
		void queryClient.invalidateQueries(batchListsFilters());
	};

	return {
		deleteBatch: (request) =>
			mutation.mutate(undefined, {
				onSuccess: () => deleted(request),
				onError: (error) => {
					if (httpStatusOf(error) === 404) {
						deleted(request);
						return;
					}
					const failure = deleteFailureOf(error);
					if (failure === 'has_review_items') {
						void queryClient.invalidateQueries(batchQueriesFilters(params.batchId));
					}
					showToastV2({ state: 'error', title: tCapManage(FAILURE_TOAST_KEYS[failure]) });
				},
			}),
		isDeleting: mutation.isPending,
	};
};
